import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import ServiceImageCarousel from '@/components/ServiceImageCarousel';
import { 
  Users, 
  Printer, 
  Car, 
  GraduationCap, 
  ArrowRight, 
  CheckCircle2,
  Building2,
  Target,
  Award,
  FileText,
  Handshake,
  Newspaper,
  Phone,
  Mail,
  Calendar,
  Send,
  Loader2,
  ClipboardList
} from 'lucide-react';
import { getCategories, getSettings } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';

const iconMap = {
  Users: Users,
  Printer: Printer,
  Car: Car,
  GraduationCap: GraduationCap,
};

// Ana hizmet kategorileri için carousel 2. görselleri
// İlk görsel category.image_url'den gelecek, 2. görsel buradan
const serviceSecondImages = {
  'İnsan Kaynakları Hizmetleri': 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/djup5xa1_Gemini_Generated_Image_ouq23aouq23aouq2%281%29.png',
  'Dijital Baskı Çözümleri': 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i20bqspo_Gemini_Generated_Image_110eog110eog110e%20%281%29.png',
  'Araç Kiralama Hizmetleri': 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/zkvv5rx7_WhatsApp%20Image%202026-03-28%20at%2012.24.31.jpeg',
  'Eğitim ve Danışmanlık': 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/ij0ob75m_ChatGPT%20Image%2028%20Mar%202026%2015_20_13.png'
};

// Hero bölümündeki 4 kart için carousel görselleri
// Her kart için [ana görsel, 2. görsel] şeklinde
const heroCarouselImages = {
  'İnsan Kaynakları': {
    images: [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/8xuwsih2_ChatGPT%20Image%2017%20Mar%202026%2015_00_32.png'
    ],
    secondImage: 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/djup5xa1_Gemini_Generated_Image_ouq23aouq23aouq2%281%29.png'
  },
  'Dijital Baskı': {
    images: [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/vi7ky692_Gemini_Generated_Image_9hbmth9hbmth9hbm.png'
    ],
    secondImage: 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i20bqspo_Gemini_Generated_Image_110eog110eog110e%20%281%29.png'
  },
  'Araç Kiralama': {
    images: [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/grf4qrup_Gemini_Generated_Image_ierh0nierh0nierh.png'
    ],
    secondImage: 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/zkvv5rx7_WhatsApp%20Image%202026-03-28%20at%2012.24.31.jpeg'
  },
  'Eğitim ve Danışmanlık': {
    images: [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/gd9937yd_Gemini_Generated_Image_1jblzf1jblzf1jbl.png'
    ],
    secondImage: 'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/ij0ob75m_ChatGPT%20Image%2028%20Mar%202026%2015_20_13.png'
  }
};

const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [heroSettings, setHeroSettings] = useState(null);
  const [aboutSettings, setAboutSettings] = useState(null);
  const [ctaSettings, setCtaSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Teklif formu state'leri
  const [quoteFormLoading, setQuoteFormLoading] = useState(false);
  const [quoteFormSubmitted, setQuoteFormSubmitted] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    company_name: '',
    contact_person: '',
    phone: '',
    email: '',
    staff_count: '',
    transportation: '',
    clothing: '',
    food: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, heroRes, aboutRes, ctaRes] = await Promise.all([
          getCategories(),
          getSettings('hero'),
          getSettings('about'),
          getSettings('cta')
        ]);
        setCategories(catRes.data);
        setHeroSettings(heroRes.data?.value || {});
        setAboutSettings(aboutRes.data?.value || {});
        setCtaSettings(ctaRes.data?.value || {});
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Teklif formu submit
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    if (!quoteForm.company_name || !quoteForm.contact_person || !quoteForm.phone || !quoteForm.email || !quoteForm.staff_count) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }
    
    setQuoteFormLoading(true);
    try {
      await api.post('/quote-request', quoteForm);
      setQuoteFormSubmitted(true);
      toast.success('Teklif talebiniz alındı! En kısa sürede size dönüş yapacağız.');
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setQuoteFormLoading(false);
    }
  };

  // Fix old brand name if present in database
  const fixBrandName = (text) => {
    if (!text) return text;
    return text
      .replace(/Eta Global Danışmanlık/g, 'Eta Global Group')
      .replace(/Profesyonel Danışmanlık Hizmetleri/g, 'Profesyonel İK Çözümleri');
  };

  const heroDefaults = {
    badge: 'Profesyonel İK Çözümleri',
    title: 'Doğru İşe, Doğru İnsan Kaynağı',
    subtitle: 'Eta Global Group olarak, işletmelerin insan kaynağı süreçlerini kolaylaştıran profesyonel çözümler sunuyoruz.',
    stats: [
      { value: '10+', label: 'Yıllık Deneyim' },
      { value: '500+', label: 'Mutlu Müşteri' },
      { value: '4', label: 'Hizmet Alanı' }
    ],
    images: [
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/8xuwsih2_ChatGPT%20Image%2017%20Mar%202026%2015_00_32.png',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/vi7ky692_Gemini_Generated_Image_9hbmth9hbmth9hbm.png',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/i20bqspo_Gemini_Generated_Image_110eog110eog110e%20%281%29.png',
      'https://customer-assets.emergentagent.com/job_personnel-staffing/artifacts/gd9937yd_Gemini_Generated_Image_1jblzf1jblzf1jbl.png'
    ]
  };

  // Default values (sanitized against legacy brand text from DB)
  const heroRaw = (heroSettings && Object.keys(heroSettings).length > 0) ? heroSettings : heroDefaults;
  const hero = {
    ...heroRaw,
    badge: fixBrandName(heroRaw.badge) || heroDefaults.badge,
    title: fixBrandName(heroRaw.title),
    subtitle: fixBrandName(heroRaw.subtitle)
  };

  const aboutRaw = aboutSettings || {
    subtitle: 'Güvenilir İş Ortağınız',
    description: 'Eta Global Group, personel istihdamı alanındaki deneyimi ile işletmelerin insan kaynağı süreçlerini kolaylaştıran profesyonel çözümler sunmaktadır.',
    mission: 'Doğru işe, doğru insan kaynağını yerleştirmektir.',
    image: null
  };
  
  const about = {
    ...aboutRaw,
    description: fixBrandName(aboutRaw.description),
    subtitle: fixBrandName(aboutRaw.subtitle),
    mission: fixBrandName(aboutRaw.mission)
  };

  const cta = ctaSettings || {
    title: 'Projeleriniz İçin Bize Ulaşın',
    subtitle: 'İnsan kaynakları ihtiyaçlarınız için profesyonel destek almak ister misiniz? Hemen iletişime geçin, size özel çözümler sunalım.',
    buttonText: 'Bizimle İletişime Geçin'
  };

  const defaultCategories = [
    {
      id: '1',
      title: 'İnsan Kaynakları Hizmetleri',
      description: 'Profesyonel insan kaynakları çözümleri ile doğru yeteneklere ulaşın.',
      icon: 'Users',
      image_url: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800',
      subcategories: [
        { id: '1-1', title: 'Personel Tedariki', description: 'Mavi yaka personel tedariki' },
        { id: '1-2', title: 'Seçme ve Yerleştirme', description: 'İşe alım süreç yönetimi' },
      ]
    },
    {
      id: '2',
      title: 'Dijital Baskı Çözümleri',
      description: 'Kaliteli ve hızlı dijital baskı hizmetleri.',
      icon: 'Printer',
      image_url: 'https://images.unsplash.com/photo-1562654501-a0ccc0fc3fb1?w=800',
      subcategories: []
    },
    {
      id: '3',
      title: 'Araç Kiralama Hizmetleri',
      description: 'Kurumsal ve bireysel araç kiralama çözümleri.',
      icon: 'Car',
      image_url: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800',
      subcategories: []
    },
    {
      id: '4',
      title: 'Eğitim ve Danışmanlık',
      description: 'Satış, pazarlama ve kurumsal gelişim eğitimleri.',
      icon: 'GraduationCap',
      image_url: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=800',
      subcategories: []
    },
  ];

  const displayCategories = categories.length > 0 ? categories : defaultCategories;

  // Parse title for highlight
  const renderTitle = () => {
    const title = hero.title || 'Doğru İşe, Doğru İnsan Kaynağı';
    if (title.includes(',')) {
      const parts = title.split(',');
      return (
        <>
          {parts[0]},{' '}
          <span className="text-[#005CB2]">{parts[1]}</span>
        </>
      );
    }
    return title;
  };

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-slate-50" data-testid="hero-section">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#005CB2]/5 via-transparent to-[#3D94F6]/5" />
          <div className="absolute top-20 right-0 w-[600px] h-[600px] bg-[#005CB2]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#3D94F6]/5 rounded-full blur-3xl" />
        </div>
        
        <div className="eta-container relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left Content */}
            <div className="space-y-8 animate-slide-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#005CB2]/10 rounded-full">
                <span className="w-2 h-2 bg-[#005CB2] rounded-full animate-pulse" />
                <span className="text-sm font-medium text-[#005CB2]">
                  {hero.badge}
                </span>
              </div>
              
              <h1 className="eta-heading-1 text-slate-900" data-testid="hero-title">
                {renderTitle()}
              </h1>
              
              <p className="text-lg text-slate-600 leading-relaxed max-w-lg" data-testid="hero-subtitle">
                {hero.subtitle}
              </p>
              
              <div className="flex flex-wrap gap-4">
                <Link to="/personel-talep">
                  <Button 
                    data-testid="hero-personnel-btn"
                    size="lg" 
                    className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Personel Talep Formu
                  </Button>
                </Link>
                <Link to="/hizmetler">
                  <Button 
                    data-testid="hero-services-btn"
                    size="lg" 
                    variant="outline"
                    className="border-[#005CB2] text-[#005CB2] hover:bg-[#005CB2] hover:text-white gap-2"
                  >
                    Hizmetlerimiz
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
              </div>

              {/* Stats */}
              <div className="flex flex-wrap gap-8 pt-8 border-t border-slate-200">
                {(hero.stats || []).map((stat, index) => (
                  <div key={index}>
                    <p className="text-3xl font-bold text-[#005CB2]">{stat.value}</p>
                    <p className="text-sm text-slate-500">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right Content - Image Grid with Carousel */}
            <div className="relative hidden lg:block">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  {/* İnsan Kaynakları Hizmetleri - Carousel */}
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl relative group">
                    <ServiceImageCarousel 
                      images={
                        heroCarouselImages['İnsan Kaynakları'].secondImage 
                          ? [heroCarouselImages['İnsan Kaynakları'].images[0], heroCarouselImages['İnsan Kaynakları'].secondImage]
                          : heroCarouselImages['İnsan Kaynakları'].images
                      }
                      alt="İnsan Kaynakları"
                      interval={3000}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-[#005CB2] to-[#003d75] p-3 z-30 pointer-events-none">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-bold bg-white/20 px-2 py-0.5 rounded">ETA</span>
                        <span className="text-white text-sm font-semibold">İnsan Kaynakları Hizmetleri</span>
                      </div>
                    </div>
                  </div>
                  {/* Dijital Baskı Çözümleri - Carousel */}
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-lg relative group">
                    <ServiceImageCarousel 
                      images={
                        heroCarouselImages['Dijital Baskı'].secondImage 
                          ? [heroCarouselImages['Dijital Baskı'].images[0], heroCarouselImages['Dijital Baskı'].secondImage]
                          : heroCarouselImages['Dijital Baskı'].images
                      }
                      alt="Dijital Baskı"
                      interval={3000}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-[#1e88e5] to-[#0d47a1] p-3 z-30 pointer-events-none">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-bold bg-white/20 px-2 py-0.5 rounded">ETA</span>
                        <span className="text-white text-sm font-semibold">Dijital Baskı Çözümleri</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  {/* Araç Kiralama Hizmetleri - Carousel */}
                  <div className="aspect-square rounded-2xl overflow-hidden shadow-lg relative group">
                    <ServiceImageCarousel 
                      images={
                        heroCarouselImages['Araç Kiralama'].secondImage 
                          ? [heroCarouselImages['Araç Kiralama'].images[0], heroCarouselImages['Araç Kiralama'].secondImage]
                          : heroCarouselImages['Araç Kiralama'].images
                      }
                      alt="Araç Kiralama"
                      interval={3000}
                      className="object-right"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-[#37474f] to-[#263238] p-3 z-30 pointer-events-none">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-bold bg-white/20 px-2 py-0.5 rounded">ETA</span>
                        <span className="text-white text-sm font-semibold">Araç Kiralama Hizmetleri</span>
                      </div>
                    </div>
                  </div>
                  {/* Eğitim ve Danışmanlık - Carousel */}
                  <div className="aspect-[4/5] rounded-2xl overflow-hidden shadow-xl relative group">
                    <ServiceImageCarousel 
                      images={
                        heroCarouselImages['Eğitim ve Danışmanlık'].secondImage 
                          ? [heroCarouselImages['Eğitim ve Danışmanlık'].images[0], heroCarouselImages['Eğitim ve Danışmanlık'].secondImage]
                          : heroCarouselImages['Eğitim ve Danışmanlık'].images
                      }
                      alt="Eğitim ve Danışmanlık"
                      interval={3000}
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-[#00796b] to-[#004d40] p-3 z-30 pointer-events-none">
                      <div className="flex items-center gap-2">
                        <span className="text-white text-xs font-bold bg-white/20 px-2 py-0.5 rounded">ETA</span>
                        <span className="text-white text-sm font-semibold">Eğitim ve Danışmanlık</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="eta-section bg-white" data-testid="services-section">
        <div className="eta-container">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="eta-caption text-[#005CB2] mb-4">HİZMETLERİMİZ</p>
            <h2 className="eta-heading-2 mb-6">
              Profesyonel Çözümlerimiz
            </h2>
            <p className="eta-body">
              Eta Global Group olarak dört ana kategoride hizmet vermekteyiz. 
              Her bir alanda uzman kadromuzla yanınızdayız.
            </p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-80 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {displayCategories.map((category, index) => {
                const IconComponent = iconMap[category.icon] || Users;
                return (
                  <Card 
                    key={category.id} 
                    data-testid={`service-card-${index}`}
                    className="service-card border-slate-200 overflow-hidden group"
                  >
                    <CardContent className="p-0">
                      <div className="flex flex-col md:flex-row">
                        {/* Image Carousel */}
                        <div className="md:w-2/5 aspect-video md:aspect-auto md:min-h-[280px]">
                          <ServiceImageCarousel 
                            images={
                              serviceSecondImages[category.title] 
                                ? [category.image_url, serviceSecondImages[category.title]]
                                : [category.image_url]
                            }
                            alt={category.title}
                            interval={3000}
                          />
                        </div>
                        
                        {/* Content */}
                        <div className="md:w-3/5 p-6 flex flex-col">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-[#005CB2]/10 rounded-lg flex items-center justify-center group-hover:bg-[#005CB2] transition-colors">
                              <IconComponent className="w-6 h-6 text-[#005CB2] group-hover:text-white transition-colors" />
                            </div>
                            <h3 className="text-lg font-bold text-[#005CB2]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                              {category.title}
                            </h3>
                          </div>
                          
                          <p className="text-sm text-slate-600 mb-4 line-clamp-2">
                            {category.description}
                          </p>

                          {/* Subcategories Accordion */}
                          {category.subcategories && category.subcategories.length > 0 && (
                            <Accordion type="single" collapsible className="mb-4">
                              <AccordionItem value="subcategories" className="border-none">
                                <AccordionTrigger 
                                  data-testid={`accordion-trigger-${index}`}
                                  className="text-sm text-[#005CB2] hover:no-underline py-2 px-0"
                                >
                                  Alt Hizmetler ({category.subcategories.length})
                                </AccordionTrigger>
                                <AccordionContent>
                                  <ul className="space-y-2 pt-2">
                                    {category.subcategories.map((sub) => (
                                      <li 
                                        key={sub.id} 
                                        className="flex items-start gap-2 text-sm text-slate-600"
                                      >
                                        <CheckCircle2 className="w-4 h-4 text-[#3D94F6] mt-0.5 flex-shrink-0" />
                                        <span>{sub.title}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </AccordionContent>
                              </AccordionItem>
                            </Accordion>
                          )}

                          <Link 
                            to="/hizmetler" 
                            className="mt-auto inline-flex items-center gap-2 text-sm font-medium text-[#005CB2] hover:gap-3 transition-all"
                          >
                            Detaylı Bilgi
                            <ArrowRight className="w-4 h-4" />
                          </Link>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="text-center mt-12">
            <Link to="/hizmetler">
              <Button 
                data-testid="view-all-services-btn"
                size="lg"
                className="bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
              >
                Tüm Hizmetleri Görüntüle
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview Section */}
      <section className="eta-section bg-slate-50" data-testid="about-section">
        <div className="eta-container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-xl bg-gradient-to-br from-[#005CB2]/20 to-[#005CB2]/5">
                {about.image ? (
                  <img 
                    src={about.image} 
                    alt="Eta Global Ekibi"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-20 h-20 bg-[#005CB2]/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Users className="w-10 h-10 text-[#005CB2]" />
                      </div>
                      <p className="text-[#005CB2] font-semibold">Eta Global Group</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Overlay Card */}
              <div className="absolute -bottom-6 -right-6 bg-[#005CB2] text-white p-6 rounded-xl shadow-xl max-w-xs">
                <p className="text-3xl font-bold mb-2">{hero.stats?.[0]?.value || '10+'}</p>
                <p className="text-sm text-white/80">
                  Yıllık sektör deneyimi ile profesyonel hizmet
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-6">
              <p className="eta-caption text-[#005CB2]">HAKKIMIZDA</p>
              <h2 className="eta-heading-2">
                {about.subtitle}
              </h2>
              <p className="eta-body">
                {about.description}
              </p>
              <p className="eta-body">
                Temel prensibimiz: <strong>{about.mission}</strong>
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#005CB2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-5 h-5 text-[#005CB2]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Kurumsal</p>
                    <p className="text-sm text-slate-500">Profesyonel yaklaşım</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#005CB2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Target className="w-5 h-5 text-[#005CB2]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Hedef Odaklı</p>
                    <p className="text-sm text-slate-500">Sonuç garantisi</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-[#005CB2]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-5 h-5 text-[#005CB2]" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-800">Kaliteli</p>
                    <p className="text-sm text-slate-500">Yüksek standart</p>
                  </div>
                </div>
              </div>

              <Link to="/hakkimizda">
                <Button 
                  data-testid="about-more-btn"
                  className="mt-4 bg-[#005CB2] hover:bg-[#004B92] text-white gap-2"
                >
                  Daha Fazla Bilgi
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Belgelerimiz Section */}
      <section className="py-20 bg-white" data-testid="documents-section">
        <div className="eta-container">
          <div className="text-center mb-12">
            <p className="text-[#005CB2] font-semibold tracking-wider mb-3">BELGELERİMİZ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Kurumsal Belgelerimiz
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Kalite standartlarımızı ve yasal uygunluğumuzu belgeleyen sertifikalarımız
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <div 
              data-testid="iskur-badge-ozel-istihdam"
              className="group bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100 hover:shadow-xl hover:border-[#005CB2]/20 transition-all duration-300 text-center flex flex-col items-center"
            >
              <div className="h-32 flex items-center justify-center mb-6">
                <img 
                  src="/iskur-ozel-istihdam-burosu.png" 
                  alt="İŞKUR Özel İstihdam Bürosu Belgesi"
                  className="h-full w-auto object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Özel İstihdam Bürosu</h3>
              <p className="text-sm text-slate-600">İŞKUR onaylı Özel İstihdam Bürosu yetki belgemiz bulunmaktadır.</p>
            </div>

            <div 
              data-testid="iskur-badge-gecici-is"
              className="group bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-slate-100 hover:shadow-xl hover:border-[#005CB2]/20 transition-all duration-300 text-center flex flex-col items-center"
            >
              <div className="h-32 flex items-center justify-center mb-6">
                <img 
                  src="/iskur-gecici-is-iliskisi.png" 
                  alt="İŞKUR Geçici İş İlişkisi Belgesi"
                  className="h-full w-auto object-contain"
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Geçici İş İlişkisi Belgesi</h3>
              <p className="text-sm text-slate-600">İŞKUR onaylı Geçici İş İlişkisi (ödünç işçilik) yetki belgemiz bulunmaktadır.</p>
            </div>
          </div>

          <div className="text-center mt-10">
            <Link to="/belgelerimiz">
              <Button className="bg-[#005CB2] hover:bg-[#004a91] gap-2">
                Tüm Belgelerimiz
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Teklif Alma Formu Section */}
      <section className="py-20 bg-gradient-to-br from-[#005CB2] to-[#003d75]" data-testid="quote-form-section">
        <div className="eta-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Sol taraf - Başlık ve bilgi */}
            <div className="text-white">
              <p className="text-white/70 font-semibold tracking-wider mb-3">TEKLİF ALIN</p>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Personel İhtiyacınız İçin Hızlı Teklif
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Formu doldurun, size özel teklif hazırlayalım. Uzman ekibimiz en kısa sürede sizinle iletişime geçecektir.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/90">Ücretsiz keşif ve analiz</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/90">24 saat içinde geri dönüş</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/90">Esnek ve rekabetçi fiyatlandırma</span>
                </div>
              </div>
            </div>

            {/* Sağ taraf - Form */}
            <div className="bg-white rounded-2xl shadow-2xl p-8">
              {quoteFormSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">Talebiniz Alındı!</h3>
                  <p className="text-slate-600 mb-6">En kısa sürede sizinle iletişime geçeceğiz.</p>
                  <Button 
                    onClick={() => {
                      setQuoteFormSubmitted(false);
                      setQuoteForm({
                        company_name: '',
                        contact_person: '',
                        phone: '',
                        email: '',
                        staff_count: '',
                        transportation: '',
                        clothing: '',
                        food: '',
                        notes: ''
                      });
                    }}
                    variant="outline"
                  >
                    Yeni Talep Oluştur
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleQuoteSubmit} className="space-y-5">
                  <div className="flex items-center gap-3 mb-6">
                    <ClipboardList className="w-6 h-6 text-[#005CB2]" />
                    <h3 className="text-xl font-bold text-slate-900">Teklif Talep Formu</h3>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="quote_company" className="font-medium">Firma Adı <span className="text-red-500">*</span></Label>
                      <Input
                        id="quote_company"
                        value={quoteForm.company_name}
                        onChange={(e) => setQuoteForm({...quoteForm, company_name: e.target.value})}
                        placeholder="Firma adınız"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote_contact" className="font-medium">Yetkili Adı <span className="text-red-500">*</span></Label>
                      <Input
                        id="quote_contact"
                        value={quoteForm.contact_person}
                        onChange={(e) => setQuoteForm({...quoteForm, contact_person: e.target.value})}
                        placeholder="Ad Soyad"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="quote_phone" className="font-medium">Telefon <span className="text-red-500">*</span></Label>
                      <Input
                        id="quote_phone"
                        type="tel"
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                        placeholder="05XX XXX XX XX"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="quote_email" className="font-medium">E-Posta <span className="text-red-500">*</span></Label>
                      <Input
                        id="quote_email"
                        type="email"
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                        placeholder="ornek@firma.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote_staff" className="font-medium">İhtiyaç Duyulan Personel Sayısı <span className="text-red-500">*</span></Label>
                    <Select value={quoteForm.staff_count} onValueChange={(v) => setQuoteForm({...quoteForm, staff_count: v})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seçiniz" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-10">1-10 Kişi</SelectItem>
                        <SelectItem value="11-50">11-50 Kişi</SelectItem>
                        <SelectItem value="51-100">51-100 Kişi</SelectItem>
                        <SelectItem value="101-500">101-500 Kişi</SelectItem>
                        <SelectItem value="500+">500+ Kişi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3">
                    <Label className="font-medium">Ulaşım Kime Ait?</Label>
                    <RadioGroup 
                      value={quoteForm.transportation} 
                      onValueChange={(v) => setQuoteForm({...quoteForm, transportation: v})}
                      className="flex flex-wrap gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="firma" id="trans_firma" />
                        <Label htmlFor="trans_firma" className="cursor-pointer">Firmaya Ait</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="personel" id="trans_personel" />
                        <Label htmlFor="trans_personel" className="cursor-pointer">Personele Ait</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="eta" id="trans_eta" />
                        <Label htmlFor="trans_eta" className="cursor-pointer">ETA Sağlasın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-3">
                    <Label className="font-medium">Kıyafet Kime Ait?</Label>
                    <RadioGroup 
                      value={quoteForm.clothing} 
                      onValueChange={(v) => setQuoteForm({...quoteForm, clothing: v})}
                      className="flex flex-wrap gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="firma" id="cloth_firma" />
                        <Label htmlFor="cloth_firma" className="cursor-pointer">Firmaya Ait</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="personel" id="cloth_personel" />
                        <Label htmlFor="cloth_personel" className="cursor-pointer">Personele Ait</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="eta" id="cloth_eta" />
                        <Label htmlFor="cloth_eta" className="cursor-pointer">ETA Sağlasın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-3">
                    <Label className="font-medium">Yemek Kime Ait?</Label>
                    <RadioGroup 
                      value={quoteForm.food} 
                      onValueChange={(v) => setQuoteForm({...quoteForm, food: v})}
                      className="flex flex-wrap gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="firma" id="food_firma" />
                        <Label htmlFor="food_firma" className="cursor-pointer">Firmaya Ait</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="personel" id="food_personel" />
                        <Label htmlFor="food_personel" className="cursor-pointer">Personele Ait</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="eta" id="food_eta" />
                        <Label htmlFor="food_eta" className="cursor-pointer">ETA Sağlasın</Label>
                      </div>
                    </RadioGroup>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quote_notes" className="font-medium">Ek Notlar</Label>
                    <Textarea
                      id="quote_notes"
                      value={quoteForm.notes}
                      onChange={(e) => setQuoteForm({...quoteForm, notes: e.target.value})}
                      placeholder="Varsa eklemek istediğiniz bilgiler..."
                      rows={3}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    disabled={quoteFormLoading}
                    className="w-full bg-[#005CB2] hover:bg-[#004a91] py-6 text-lg font-semibold gap-2"
                  >
                    {quoteFormLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Gönderiliyor...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Teklif Talebi Gönder
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Referanslarımız Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="references-section">
        <div className="eta-container">
          <div className="text-center mb-12">
            <p className="text-[#005CB2] font-semibold tracking-wider mb-3">REFERANSLARIMIZ</p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Güvenilir İş Ortaklıkları
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Türkiye'nin önde gelen firmalarıyla uzun soluklu iş birlikleri yürütüyoruz
            </p>
          </div>
          
          <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-12">
            <div className="flex flex-col items-center text-center">
              <Handshake className="w-16 h-16 text-[#005CB2] mb-6" />
              <h3 className="text-2xl font-bold text-slate-900 mb-4">Referanslarımız Hakkında Bilgi Alın</h3>
              <p className="text-slate-600 mb-6 max-w-xl">
                10+ yıllık deneyimimizle birçok sektörde öncü firmalarla çalışmaktayız. 
                Referanslarımız ve başarı hikayelerimiz hakkında detaylı bilgi almak için 
                bizimle iletişime geçin.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/referanslarimiz">
                  <Button className="bg-[#005CB2] hover:bg-[#004a91] gap-2">
                    Detaylı Bilgi
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+905334143024">
                  <Button variant="outline" className="border-[#005CB2] text-[#005CB2] hover:bg-[#005CB2]/5 gap-2">
                    <Phone className="w-4 h-4" />
                    0533 414 30 24
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bizden Haberler Section */}
      <section className="py-16 bg-white" data-testid="news-section">
        <div className="eta-container">
          <div className="text-center">
            <Link to="/haberler">
              <Button size="lg" className="bg-[#005CB2] hover:bg-[#004a91] gap-2 px-8">
                <Newspaper className="w-5 h-5" />
                Bizden Haberler
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#005CB2]" data-testid="cta-section">
        <div className="eta-container">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {cta.title}
            </h2>
            <p className="text-lg text-white/80 mb-8">
              {cta.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/iletisim">
                <Button 
                  data-testid="cta-contact-btn"
                  size="lg" 
                  className="bg-white text-[#005CB2] hover:bg-slate-100 gap-2 w-full sm:w-auto"
                >
                  {cta.buttonText}
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/personel-talep">
                <Button 
                  data-testid="cta-personnel-btn"
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 gap-2 w-full sm:w-auto"
                >
                  Personel Talep Formu
                  <Users className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/is-basvurusu">
                <Button 
                  data-testid="cta-job-btn"
                  size="lg" 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white/10 gap-2 w-full sm:w-auto"
                >
                  İş Başvuru Formu
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
